import { AxiosResponse } from 'axios';
import {
  addMonths,
  differenceInCalendarMonths,
  isAfter,
  lastDayOfMonth,
} from 'date-fns';
import qs from 'qs';
import { PAGE_SIZE } from '~/constants';
import {
  AutomaticAnalysis,
  ContractColorsEnum,
  Contract,
  ContractForDocuments,
  ContractRelatedView,
  ContractView,
  Pagination,
  StatusExtensaoEnum,
} from '~/types';
import api from '~/utils/api';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { getMonthByNumber } from '~/utils/getMonthByNumber';
import { parseDate } from '~/utils/parseDate';
import { addFormattedPropertiesToInvoice, getCompanyById } from '..';
import {
  ContractBusinessInformation,
  Invoices,
  InvoiceStatus,
} from '~/types/businesseInformation';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export function addFormattedPropertiesToContract(
  contract: Contract,
): ContractView {
  const invoices =
    contract.faturas?.map(fatura => addFormattedPropertiesToInvoice(fatura)) ??
    [];

  const contratosRelated = contract.contratos_relacionados?.map(item => {
    return addFormattedPropertiesToContractRelated(item);
  });

  const status = invoices?.every(invoice => invoice.status);
  const contractDueDate = new Date(
    `${contract.dt_encerramentocontrato}T00:00:00Z`,
  );

  return {
    ...contract,
    faturas: invoices,
    formattedInitialValue: formatCurrency(
      Number(contract.vr_totalinicialcontrato),
    ),
    formattedMensalValue: formatCurrency(
      Number(contract.vr_totalmensalcontrato),
    ),
    formattedMensalAtualValue: formatCurrency(Number(contract.vr_mensal_atual)),
    formattedInitialDate: formatInTimeZone(
      `${contract.dt_inicialcontrato}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedEndDate: formatInTimeZone(
      `${contract.dt_encerramentocontrato}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedDueDate: formatInTimeZone(
      `${contract.vencimento_final}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedMonth:
      Number(contract?.mm_ajusteinflacao) === 0 || !contract?.mm_ajusteinflacao
        ? '-'
        : getMonthByNumber(Number(contract?.mm_ajusteinflacao) - 1),
    formattedStatus:
      Object.values(StatusExtensaoEnum)[contract.status_proposta],
    statusColorScheme:
      Object.values(ContractColorsEnum)[contract.status_proposta],
    /* 
    status,
    formattedStatus: isAfter(new Date(), latestDueDate)
      ? 'Encerrado'
      : status
      ? 'Em Dia'
      : 'Em Atraso', 
    */
    colorScheme:
      contract?.status_pgto === 'Encerrado'
        ? 'purple'
        : contract?.status_pgto === 'Em Dia'
        ? 'green'
        : 'red',
    quantity: contract?.qtd_equipamentos || 0,
    formattedTotalValue: formatCurrency(Number(contract?.vr_totalcontrato)),
    status_pgto: contract?.status_pgto,
    contratos_relacionados: contratosRelated,
    stFaturamentoDesc:
      contract?.st_faturamento === 0 ? 'LIBERADO' : 'AGUARDANDO REAJUSTE',
    formattedVencimentoFinal: contract.vencimento_final
      ? formatInTimeZone(`${contract.vencimento_final}T00:00:00Z`, 'dd/MM/yyyy')
      : '',
    formattedTipoSituacao: contract.tp_situacao === '1' ? 'INATIVO' : 'ATIVO',
    colorSchemeTipoSituacao: contract.tp_situacao === '1' ? 'red' : 'green',
    formattedSituacaoFaturamento:
      contract.st_faturamento === 0 ? 'Liberado' : 'Aguardando reajuste',
    colorSchemeSituacaoFaturamento:
      contract.st_faturamento === 0 ? 'green' : 'red',
    pode_rescisao: contract.vencimento_final
      ? isAfter(parseDate(contract.vencimento_final), new Date())
      : false,
  };
}

export function addFormattedPropertiesToContractRelated(
  contract: Contract,
): ContractRelatedView {
  return {
    ...contract,
    formattedVencimentoFinal: contract.vencimento_final
      ? formatInTimeZone(`${contract.vencimento_final}T00:00:00Z`, 'dd/MM/yyyy')
      : '',
    formattedTotalValue: formatCurrency(Number(contract?.vr_totalcontrato)),
  };
}

export async function getContracts(
  page: number,
  search: string,
  status: string,
  orderby: string,
  order: string,
  faturamento: string,
  seguro: string,
  certificado: string,
  query?: any,
  group?: string,
  headOffice?: string,
  branch?: string,
  page_size?: number,
): Promise<Pagination<ContractView>> {
  const { data } = await api.get<Pagination<Contract>>('/contratos', {
    params: {
      page,
      size: page_size || PAGE_SIZE,
      orderby,
      order,
      group,
      headOffice,
      branch,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(faturamento ? { faturamento } : {}),
      ...(seguro ? { seguro } : {}),
      ...(certificado ? { certificado_zero_carbono: certificado } : {}),
      ...{ ...query },
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const contracts = data.results.map(contract =>
    addFormattedPropertiesToContract(contract),
  );

  return {
    ...data,
    results: contracts,
  };
}

export async function getContractsForDocuments(
  search: string,
  group?: string,
  headOffice?: string,
  branch?: string,
): Promise<ContractForDocuments[]> {
  const { data } = await api.get<ContractForDocuments[]>(
    '/contratos/contratos-documentos',
    {
      params: {
        group,
        headOffice,
        branch,
        ...(search ? { search } : {}),
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  return data;
}

export async function getContractById(id: number): Promise<ContractView> {
  const { data } = await api.get<Contract>(`/contratos/${id}`);
  const company = await getCompanyById(data.empresas.id_empresa);

  const contract = addFormattedPropertiesToContract(data);

  /*
  const equipmentIds =
    data?.equipamentos.map(equipment => equipment.id_equipamento) || [];

  let equipamentos = [] as EquipmentView[];

  if (equipmentIds.length)
    equipamentos = await getEquipmentsByArrayOfIds(equipmentIds);
  */

  const extensoes = data?.extensoes?.map(extensao => ({
    ...extensao,
    formattedMonthlyValue: formatCurrency(
      Number(extensao.vr_totalmensalinstrumento),
    ),
    formattedTotalValue: formatCurrency(
      Number(extensao.vr_totalinicialinstrumento),
    ),
    formattedInitialDate: formatInTimeZone(
      `${extensao.dt_inicialinstrumento}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
    formattedMonth:
      Number(extensao?.mm_ajusteinflacao) === 0
        ? '-'
        : getMonthByNumber(Number(extensao?.mm_ajusteinflacao) - 1),
    differenceInMonths: differenceInCalendarMonths(
      parseDate(extensao.dt_encerramentoinstrumento),
      lastDayOfMonth(
        addMonths(parseDate(`${extensao.dt_inicialinstrumento}T00:00:00Z`), -1),
      ),
      // parseDate(extensao.dt_inicialinstrumento),
    ),
    vigente: parseDate(extensao.dt_encerramentoinstrumento) >= new Date(),
  }));

  const formattedEquipments = data?.formattedEquipments;
  /* equipamentos.reduce((acc, curr) => {
    const criteria = (sum: SummarizedEquipments) =>
      sum.nm_tipoequipamento === curr.tipo_equipamento?.nm_tipoequipamento &&
      sum.nm_modeloequipamento ===
        curr.modelo_equipamento?.nm_modeloequipamento;

    const alreadyIn = acc.find(sum => criteria(sum));

    const alreadyInIndex = acc.findIndex(sum => criteria(sum));

    if (!alreadyIn) {
      acc.push({
        nm_tipoequipamento: curr.tipo_equipamento?.nm_tipoequipamento,
        nm_modeloequipamento: curr.modelo_equipamento?.nm_modeloequipamento,
        quantity: 1,
      });
    } else {
      const updatedAlreadyIn = {
        ...alreadyIn,
        quantity: alreadyIn.quantity + 1,
      };

      acc[alreadyInIndex] = updatedAlreadyIn;
    }

    return acc;
  }, [] as any[]);
   data?.equipamentos.map(eq => ({
    nm_modeloequipamento: eq.nm_modeloequipamento,
    nm_tipoequipamento: eq.nm_tipoequipamento,
    quantity: eq.quantity,
  })); */

  return {
    ...contract,
    formattedEquipments,
    empresas: company,
    extensoes,
  };
}

export async function deleteDummies(): Promise<AxiosResponse<any>> {
  return api.post('/reset/contratos');
}

export async function resetContracts(): Promise<AxiosResponse<any>> {
  return api.post('/truncate/contratos');
}

export async function getExtensionContractDocument(
  id: string,
  status: number,
): Promise<AxiosResponse<any>> {
  const url =
    status === 4
      ? `/contratos/${id}/documento_extensao_assinado/`
      : `/contratos/${id}/gerar_documento_extensao/`;
  return api.get(url, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getContractBusinessInformation(
  id: string,
): Promise<ContractBusinessInformation[]> {
  const url = `/contratos/${id}/informacoes_de_comissao`;
  const { data } = await api.get<ContractBusinessInformation[]>(url);

  const businessInformation = data.map((item: ContractBusinessInformation) => {
    const { comissionado, faturas } = item;

    return {
      comissionado: {
        ...comissionado,
        total_de_comissao: formatCurrency(
          Number(comissionado.total_de_comissao),
        ),
        valor: formatCurrency(Number(comissionado.valor)),
      },
      faturas: faturas.map((fatura: Invoices) => ({
        ...fatura,
        percentual_de_comissao: fatura.percentual_de_comissao.replace('.', ','),
        valor_comissao: formatCurrency(Number(fatura.valor_comissao)),
        status:
          fatura.status === InvoiceStatus.EM_ABERTO
            ? 'EM ABERTO'
            : fatura.status === InvoiceStatus.PAGO
            ? 'PAGA'
            : 'VENCIDA',
        dt_faturamento: formatInTimeZone(
          `${fatura.dt_faturamento}T00:00:00Z`,
          'dd/MM/yyyy',
        ),
        status_badge:
          fatura.status === InvoiceStatus.EM_ABERTO
            ? 'purple'
            : fatura.status === InvoiceStatus.PAGO
            ? 'green'
            : 'red',
      })),
    };
  });

  return businessInformation;
}

export async function getContractsXlsx(
  page: number,
  search: string,
  status: string,
  orderby: string,
  order: string,
  faturamento: string,
  seguro: string,
  certificado: string,
  query?: QueryFilterContext,
  group?: string,
): Promise<AxiosResponse<any>> {
  const url = '/contratos/xlsx';
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return api.get(url, {
    params: {
      ...(page && { page }),
      ...(search && { search }),
      ...(status && { status }),
      ...(orderby && { orderby }),
      ...(order && { order }),
      ...(faturamento && { faturamento }),
      ...(seguro && { seguro }),
      ...(certificado && { certificado_zero_carbono: certificado }),
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
      ...(group && { group }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getContractEquipamentsXlsx(
  id_contrato: String,
): Promise<AxiosResponse<any>> {
  const url = `/contratos/${id_contrato}/exportar_equipamento`;
  return api.get(url, {
    params: {},
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}

export async function updateExtensionZeroCarbon(
  id_contrato: string,
  id_adendoaditivo: string,
  status: boolean,
): Promise<AxiosResponse<any>> {
  return api.post(`/contratos/${id_contrato}/check_projeto_zero_carbono`, {
    projeto_zero_carbono: status,
    id_adendoaditivo,
  });
}

export async function getContractAgreement(
  id_contrato: String,
): Promise<AxiosResponse<any>> {
  const url = `/contratos/${id_contrato}/download_termo_contrato`;
  return api.get(url, {
    headers: {
      'Content-Type': 'application/docx',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getAutomaticAnalysis(
  id_contrato: string,
): Promise<AutomaticAnalysis> {
  const { data } = await api.get<AutomaticAnalysis>(
    `/contratos/${id_contrato}/analise-automatica`,
  );
  const most_relevant_features_arr = data.features.slice(0, 3);

  return {
    ...data,
    most_relevant_features: `${most_relevant_features_arr
      .slice(0, 2)
      .map(feature => {
        return feature.label;
      })
      .join(', ')} e ${most_relevant_features_arr[2].label}`,
    validFeaturesCount: most_relevant_features_arr.filter(feature => {
      return feature.resultado;
    }).length,
    features: data.features.map(feature => {
      return {
        ...feature,
        formattedValue: feature.valor.toString().replace('.', ','),
        formattedLimit: feature.limiar.toString().replace('.', ','),
      };
    }),
  };
}
